import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { COEFFICIENTS_PAR_FORMULE, TARIFS_PAR_TENSION } from '../../pages/turpe/constants';
import { PdlPagination } from './PdlPagination';
import { CmdpsOverrideCell } from './CmdpsOverrideCell';
import { appliquerOverrideCMDPS } from '../../utils/calculsTurpeRapport';

/**
 * Étape 6 TURPE — extrait de CreateAnalyseTURPE.jsx pour réduire la taille
 * du composant parent. Reçoit toutes les dépendances via un objet `ctx` unique.
 *
 * Optimisation perfs multi-PDL : au-delà de 10 PDL, on n'affiche que les 10
 * premiers par défaut + recherche + bouton "Afficher tous".
 */
const SEUIL_PAGINATION = 10;

export const Step6Validation = ({ ctx }) => {
  const [searchPdl, setSearchPdl] = useState('');
  const [page, setPage] = useState(0);
  const [, forceRender] = useState(0);
  const {
    analyseType,
    calculerConsommationsParPlage,
    calculsParPDL,
    calculsRapport,
    cerOverrideRef,
    cmdpsOverrideRef,
    consommationsParPlageRef,
    determinerPlageHoraire,
    formData,
    preconisationsPuissanceParPlageRef,
    preconisationsPuissanceRef,
    puissancesMaxParPDL,
    sauvegarderBrouillon,
    setCalculsAJour,
    setCalculsParPDL,
    setCalculsRapport,
    setStep,
    step,
  } = ctx;

  return (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-2">
            Validation des Calculs TURPE
          </h2>
          <p className="text-[#687175] mb-6">
            Comparaison des composantes TURPE entre situation actuelle et situation proposée (base annuelle)
          </p>

          {Object.keys(puissancesMaxParPDL).length > 0 ? (
            <div className="space-y-8">
              {(() => {
                const allPdls = Object.entries(puissancesMaxParPDL);
                const total = allPdls.length;
                const filtres = searchPdl.trim()
                  ? allPdls.filter(([pdl]) => pdl.toLowerCase().includes(searchPdl.trim().toLowerCase()))
                  : allPdls;
                const totalPages = Math.max(1, Math.ceil(filtres.length / SEUIL_PAGINATION));
                const safePage = Math.min(page, totalPages - 1);
                const limite = filtres.slice(safePage * SEUIL_PAGINATION, (safePage + 1) * SEUIL_PAGINATION);
                return (
                  <>
                    {total > SEUIL_PAGINATION && (
                      <div className="flex flex-col gap-3 p-3 bg-[#F0F7FF] border border-[#BFD9FF] rounded-lg" data-testid="step6-perfs-bar">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="relative flex-1 min-w-[200px]">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#687175]" />
                            <input type="text" value={searchPdl} onChange={(e) => { setSearchPdl(e.target.value); setPage(0); }}
                              placeholder={`Rechercher un PDL parmi ${total}…`}
                              className="w-full pl-9 pr-3 py-2 text-sm border border-[#BFC6CC] rounded bg-white"
                              data-testid="step6-search-pdl" />
                          </div>
                          <span className="text-xs text-[#687175]">
                            {filtres.length} PDL{filtres.length > 1 ? 's' : ''}{filtres.length !== total ? ` (sur ${total})` : ''}
                          </span>
                        </div>
                        <PdlPagination page={safePage} totalPages={totalPages} total={filtres.length} pageSize={SEUIL_PAGINATION} onPage={setPage} testidPrefix="step6" />
                      </div>
                    )}
                    {limite.map(([pdl, resultatPDL]) => {
                const datasMois = resultatPDL.puissancesMaxParMois || [];
                const tousLesPoints = resultatPDL.tousLesPoints || [];
                
                if (datasMois.length === 0) return null;
                
                // Récupérer les valeurs
                const pdlConfig = formData.pdls.find(p => p.pdl === pdl);
                const puissancesActuellesParPlage = pdlConfig?.puissances_actuelles || {};
                // Prendre la puissance max parmi toutes les plages saisies, ou la puissance Pointe si disponible
                const puissanceSouscrite = Math.max(
                  puissancesActuellesParPlage?.Unique || 0,
                  puissancesActuellesParPlage?.HP || 0,
                  puissancesActuellesParPlage?.HC || 0,
                  puissancesActuellesParPlage?.Pointe || 0,
                  puissancesActuellesParPlage?.HPH || 0,
                  puissancesActuellesParPlage?.HCH || 0,
                  puissancesActuellesParPlage?.HPB || 0,
                  puissancesActuellesParPlage?.HCB || 0,
                  puissancesActuellesParPlage?.max || 0
                );
                const puissanceProposee = preconisationsPuissanceRef.current[pdl] || 0;
                const puissancesPreconiseesParPlage = preconisationsPuissanceParPlageRef.current[pdl] || {};
                const formuleActuelle = pdlConfig?.formule_actuelle || '';
                const tension = pdlConfig?.tension || 'HTA';
                const contrat = pdlConfig?.contrat || 'CARD';
                
                // Formule sélectionnée pour la simulation (peut être modifiée)
                const formuleSimulation = preconisationsPuissanceRef.current[`${pdl}_formule`] || formuleActuelle;
                
                // ========== TARIFS TURPE 7 PAR TENSION (01/08/2025) ==========
                const TARIFS_PAR_TENSION = {
                  'HTA': {
                    CG: { CARD: 499.80, Unique: 435.72 },
                    CC: 376.39,
                    CER: 2.44,
                    CMDPS_ALPHA: 0.04,
                    unite: 'kW'
                  },
                  'BTSUP': {
                    CG: { CARD: 249.84, Unique: 217.80 },
                    CC: 283.27,
                    CER: 0,
                    CMDPS_H: 12.41,
                    unite: 'kVA'
                  },
                  'BTINF': {
                    CG: { CARD: 18.00, Unique: 16.80 },
                    CC: 22.00,
                    CER: 0,
                    CMDPS_H: 0,
                    unite: 'kVA'
                  }
                };
                
                // Coefficients par formule tarifaire
                const COEFFICIENTS_PAR_FORMULE = {
                  'HTACU5 PF': { 
                    plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'],
                    b: { Pointe: 14.41, HPH: 14.41, HCH: 14.41, HPB: 12.55, HCB: 11.22 },
                    c: { Pointe: 0.0574, HPH: 0.0423, HCH: 0.0199, HPB: 0.0101, HCB: 0.0069 }
                  },
                  'HTACU5 PM': { 
                    plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'],
                    b: { Pointe: 14.41, HPH: 14.41, HCH: 14.41, HPB: 12.55, HCB: 11.22 },
                    c: { Pointe: 0.0701, HPH: 0.0405, HCH: 0.0199, HPB: 0.0101, HCB: 0.0069 }
                  },
                  'HTALU5 PF': { 
                    plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'],
                    b: { Pointe: 35.33, HPH: 32.30, HCH: 20.39, HPB: 14.33, HCB: 11.56 },
                    c: { Pointe: 0.0265, HPH: 0.0210, HCH: 0.0147, HPB: 0.0092, HCB: 0.0068 }
                  },
                  'HTALU5 PM': { 
                    plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'],
                    b: { Pointe: 38.27, HPH: 34.30, HCH: 20.39, HPB: 14.33, HCB: 11.56 },
                    c: { Pointe: 0.0315, HPH: 0.0187, HCH: 0.0147, HPB: 0.0092, HCB: 0.0068 }
                  },
                  'BTSUPCU4': { 
                    plages: ['HPH', 'HCH', 'HPB', 'HCB'],
                    b: { HPH: 17.61, HCH: 15.96, HPB: 14.56, HCB: 11.98 },
                    c: { HPH: 0.0691, HCH: 0.0421, HPB: 0.0213, HCB: 0.0152 }
                  },
                  'BTSUPLU4': { 
                    plages: ['HPH', 'HCH', 'HPB', 'HCB'],
                    b: { HPH: 30.16, HCH: 21.18, HPB: 16.64, HCB: 12.37 },
                    c: { HPH: 0.0569, HCH: 0.0347, HPB: 0.0201, HCB: 0.0149 }
                  },
                  'BTINFCU4': { 
                    plages: ['HPH', 'HCH', 'HPB', 'HCB'],
                    b: { HPH: 10.11, HCH: 10.11, HPB: 10.11, HCB: 10.11 },
                    c: { HPH: 0.0749, HCH: 0.0397, HPB: 0.0166, HCB: 0.0116 }
                  },
                  'BTINFMU4': { 
                    plages: ['HPH', 'HCH', 'HPB', 'HCB'],
                    b: { HPH: 12.12, HCH: 12.12, HPB: 12.12, HCB: 12.12 },
                    c: { HPH: 0.0700, HCH: 0.0373, HPB: 0.0161, HCB: 0.0111 }
                  },
                  'BTINFMUDT': { 
                    plages: ['HP', 'HC'],
                    b: { HP: 13.49, HC: 13.49 },
                    c: { HP: 0.0494, HC: 0.0350 }
                  },
                  'BTINFLU': { 
                    plages: ['Unique'],
                    b: { Unique: 93.13 },
                    c: { Unique: 0.0125 }
                  },
                  'BTINFCU': { 
                    plages: ['Unique'],
                    b: { Unique: 11.07 },
                    c: { Unique: 0.0484 }
                  }
                };
                
                // Déterminer la tension depuis la formule
                const getTensionFromFormule = (formule) => {
                  if (formule.startsWith('HTA')) return 'HTA';
                  if (formule.startsWith('BTSUP')) return 'BTSUP';
                  if (formule.startsWith('BTINF')) return 'BTINF';
                  return tension;
                };
                
                const tensionActuelle = getTensionFromFormule(formuleActuelle);
                const tensionSimulation = getTensionFromFormule(formuleSimulation);
                
                // Tarifs selon tension
                const tarifsActuels = TARIFS_PAR_TENSION[tensionActuelle] || TARIFS_PAR_TENSION['HTA'];
                const tarifsSimulation = TARIFS_PAR_TENSION[tensionSimulation] || TARIFS_PAR_TENSION['HTA'];
                
                // Coefficients selon formule
                const coeffsActuels = COEFFICIENTS_PAR_FORMULE[formuleActuelle] || COEFFICIENTS_PAR_FORMULE['HTACU5 PF'];
                const coeffsSimulation = COEFFICIENTS_PAR_FORMULE[formuleSimulation] || coeffsActuels;
                
                const CTA_TAUX = 0.15;
                
                // ========== CALCULS ==========
                
                // Calcul consommation totale (kWh) - extrapolé sur 1 an
                const nbJoursData = new Set(tousLesPoints.map(p => {
                  const d = new Date(p.horodate);
                  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                })).size || 1;
                const facteurAnnualisation = 365 / nbJoursData;
                
                const consoTotaleKWh = tousLesPoints.reduce((sum, p) => sum + (p.kW || 0), 0);
                
                // Consommation annuelle basée sur les consommations par plage (énergie réelle en kWh)
                // Si les consommations par plage ne sont pas encore calculées (navigation directe), les calculer
                let consoParPlagePourRatio = consommationsParPlageRef.current[pdl];
                if (!consoParPlagePourRatio || Object.keys(consoParPlagePourRatio).length === 0) {
                  if (tousLesPoints.length > 0) {
                    consoParPlagePourRatio = calculerConsommationsParPlage(tousLesPoints, formuleActuelle, pdl);
                    consommationsParPlageRef.current = {
                      ...consommationsParPlageRef.current,
                      [pdl]: consoParPlagePourRatio
                    };
                  } else {
                    consoParPlagePourRatio = {};
                  }
                }
                const totalConsoParPlage = Object.values(consoParPlagePourRatio).reduce((sum, v) => sum + (v || 0), 0);
                // Utiliser directement le total des consommations par plage comme "consommation annuelle"
                // C'est la même valeur affichée dans la section "Consommations annuelles par plage"
                const consoAnnuelleKWh = totalConsoParPlage > 0 
                  ? totalConsoParPlage
                  : consoTotaleKWh; // Fallback si pas de données par plage
                
                // Calcul énergie réactive (kVARh) - TOTAL (pour info)
                const energieReactiveTotale = tousLesPoints.reduce((sum, p) => {
                  const kVA = p.kVA || 0;
                  const kW = p.kW || 0;
                  const kVAR = Math.sqrt(Math.max(0, kVA * kVA - kW * kW));
                  return sum + kVAR;
                }, 0);
                const energieReactiveAnnuelle = energieReactiveTotale * facteurAnnualisation;
                
                // Calcul tan φ moyen (global)
                const tanPhiMoyen = consoTotaleKWh > 0 ? energieReactiveTotale / consoTotaleKWh : 0;
                
                // ========== CER : ÉNERGIE RÉACTIVE FACTURABLE (HTA uniquement) ==========
                // La facturation CER ne s'applique que pendant les plages HPH (Pointe + Heures Pleines Hiver)
                // Condition: tan φ > 0.4 pendant ces plages
                const TAN_PHI_MAX = 0.40;
                
                // Calculer l'énergie réactive et active uniquement pendant les plages HPH
                const { consoActiveHPH, consoReactiveHPH } = tousLesPoints.reduce((acc, p) => {
                  const plage = determinerPlageHoraire(p.horodate, formuleActuelle, pdl);
                  // HPH = Heures Pointe + Heures Pleines Hiver (facturation CER)
                  if (plage === 'Pointe' || plage === 'HPH') {
                    const kVA = p.kVA || 0;
                    const kW = p.kW || 0;
                    const kVAR = Math.sqrt(Math.max(0, kVA * kVA - kW * kW));
                    acc.consoActiveHPH += kW / 6; // kWh (points de 10min)
                    acc.consoReactiveHPH += kVAR / 6; // kVARh
                  }
                  return acc;
                }, { consoActiveHPH: 0, consoReactiveHPH: 0 });
                
                // Extrapoler sur l'année
                const consoActiveHPHAnnuelle = consoActiveHPH * facteurAnnualisation;
                const consoReactiveHPHAnnuelle = consoReactiveHPH * facteurAnnualisation;
                
                // tan φ pendant les plages HPH
                const tanPhiHPH = consoActiveHPHAnnuelle > 0 ? consoReactiveHPHAnnuelle / consoActiveHPHAnnuelle : 0;
                
                // Énergie réactive facturable = max(0, kVARh_HPH - 0.4 × kWh_HPH)
                // Uniquement si tan φ HPH > 0.4
                const energieReactiveFacturable = tanPhiHPH > TAN_PHI_MAX 
                  ? Math.max(0, consoReactiveHPHAnnuelle - (consoActiveHPHAnnuelle * TAN_PHI_MAX))
                  : 0;
                
                // Calculs de dépassements avancés
                // HTA: besoin de Σ(b_plage × ΔP²) pour chaque point avec le b de sa plage
                // BTSUP: compter les heures uniques avec au moins un dépassement
                // IMPORTANT: la puissance souscrite peut varier PAR PLAGE (ex: HPH=36, HCH=168).
                // Pour chaque point, on compare à la PS de la plage horaire du point.
                const calculerDepassementsAvances = (puissances, coeffs, formule, tensionType) => {
                  // Normaliser: si scalaire → dict identique pour toutes les plages connues
                  const plagesConnues = coeffs?.plages || ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB', 'HP', 'HC', 'Unique'];
                  let puissancesParPlage;
                  if (typeof puissances === 'number') {
                    if (puissances <= 0) return { nbPoints: 0, heuresUniques: 0, sommeBDeltaCarre: 0 };
                    puissancesParPlage = Object.fromEntries(plagesConnues.map(p => [p, puissances]));
                  } else {
                    puissancesParPlage = puissances || {};
                    // Si toutes les puissances sont ≤ 0, rien à calculer
                    const hasAny = Object.values(puissancesParPlage).some(v => (Number(v) || 0) > 0);
                    if (!hasAny) return { nbPoints: 0, heuresUniques: 0, sommeBDeltaCarre: 0 };
                  }

                  // Pour chaque point: puissance de référence = PS de la plage du point
                  // Formule réglementaire TURPE HTA : la racine carrée se calcule PAR PLAGE
                  // (classe temporelle), donc on accumule Σ ΔP² séparément par plage.
                  let sommeBDeltaCarre = 0; // conservé pour rétro-compat (métrique globale)
                  const sommeDeltaCarreParPlage = {}; // { plage: Σ ΔP² } — base du calcul CMDPS HTA
                  const heuresAvecDepassement = new Set();
                  let nbPoints = 0;

                  tousLesPoints.forEach(p => {
                    const plage = determinerPlageHoraire(p.horodate, formule, pdl);
                    const psPlage = Number(puissancesParPlage[plage]) || 0;
                    if (psPlage <= 0) return; // pas de PS définie pour cette plage → pas de dépassement comptabilisé
                    const valeur = tensionType === 'HTA' ? (p.kW || 0) : (p.kVA || 0);
                    if (valeur <= psPlage) return;
                    nbPoints += 1;
                    const delta = valeur - psPlage;
                    const b = coeffs?.b?.[plage] || 0;
                    sommeBDeltaCarre += b * (delta * delta);
                    sommeDeltaCarreParPlage[plage] = (sommeDeltaCarreParPlage[plage] || 0) + delta * delta;
                    const d = new Date(p.horodate);
                    const heureKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}`;
                    heuresAvecDepassement.add(heureKey);
                  });

                  return {
                    nbPoints,
                    heuresUniques: heuresAvecDepassement.size,
                    sommeBDeltaCarre,
                    sommeDeltaCarreParPlage
                  };
                };

                const depActuel = calculerDepassementsAvances(puissancesActuellesParPlage, coeffsActuels, formuleActuelle, tensionActuelle);
                // Pour la simulation, on utilise les puissances préconisées par plage si définies,
                // sinon on retombe sur le scalaire puissanceProposee (compatibilité ascendante).
                const puissancesPourDepPropose = (puissancesPreconiseesParPlage && Object.keys(puissancesPreconiseesParPlage).length > 0)
                  ? puissancesPreconiseesParPlage
                  : puissanceProposee;
                const depPropose = calculerDepassementsAvances(puissancesPourDepPropose, coeffsSimulation, formuleSimulation, tensionSimulation);

                // === Amélioration durable : réutilisation des agrégats natifs de dépassement ===
                // Si le rapport a été rechargé en édition, `tousLesPoints` provient du graphData
                // HORAIRE agrégé (résolution dégradée) → recalculer la CMDPS HTA sous-estimerait.
                // Quand les agrégats natifs (Σ ΔP² par plage) ont été stockés à la création ET que
                // la PS de la plage n'a pas changé, on réutilise ces agrégats natifs.
                if (resultatPDL.donneesDegradees && resultatPDL.depassementsStockes) {
                  // Compare les PS uniquement sur les plages réelles de la formule (ignore les
                  // clés méta comme 'max'/'unique' présentes dans calculs.puissancesActuelles).
                  const memePuissances = (a, b, plages) => {
                    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
                    const liste = (plages && plages.length) ? plages : Object.keys(a);
                    let auMoinsUne = false;
                    for (const k of liste) {
                      const va = Number(a[k] || 0);
                      const vb = Number(b[k] || 0);
                      if (va > 0 || vb > 0) auMoinsUne = true;
                      if (va !== vb) return false;
                    }
                    return auMoinsUne;
                  };
                  const dep = resultatPDL.depassementsStockes;
                  if (dep.actuel?.sommeDeltaCarreParPlage
                      && memePuissances(puissancesActuellesParPlage, resultatPDL.puissancesActuellesStockees, coeffsActuels?.plages)) {
                    depActuel.sommeDeltaCarreParPlage = dep.actuel.sommeDeltaCarreParPlage;
                    if (dep.actuel.nbPoints != null) depActuel.nbPoints = dep.actuel.nbPoints;
                    if (dep.actuel.heuresUniques != null) depActuel.heuresUniques = dep.actuel.heuresUniques;
                  }
                  if (dep.propose?.sommeDeltaCarreParPlage
                      && memePuissances(puissancesPourDepPropose, resultatPDL.puissancesProposeesStockees, coeffsSimulation?.plages)) {
                    depPropose.sommeDeltaCarreParPlage = dep.propose.sommeDeltaCarreParPlage;
                    if (dep.propose.nbPoints != null) depPropose.nbPoints = dep.propose.nbPoints;
                    if (dep.propose.heuresUniques != null) depPropose.heuresUniques = dep.propose.heuresUniques;
                  }
                }
                
                // ========== CALCUL DES COMPOSANTES EN € ==========
                
                // CG - selon tension et contrat
                const CG_Actuel = tarifsActuels.CG[contrat] || tarifsActuels.CG.CARD;
                const CG_Propose = tarifsSimulation.CG[contrat] || tarifsSimulation.CG.CARD;
                
                // CC - selon tension
                const CC_Actuel = tarifsActuels.CC;
                const CC_Propose = tarifsSimulation.CC;
                
                // CS Fixe = b_Pointe × P_Pointe + Σ(b_plage × (P_plage - P_plage_précédente))
                // Formule Excel: B2*C2 + SUMPRODUCT(B3:B6, C3:C6 - C2:C5)
                // Les puissances doivent être décroissantes ou égales de Pointe vers HCB
                const calculerCSFixe = (puissances, coeffs) => {
                  if (!coeffs || !coeffs.plages || coeffs.plages.length === 0) return 0;
                  
                  const plages = coeffs.plages;
                  let total = 0;
                  
                  // Si puissances est un nombre, convertir en objet avec la même puissance pour toutes les plages
                  const puissancesParPlage = typeof puissances === 'number' 
                    ? Object.fromEntries(plages.map(p => [p, puissances]))
                    : puissances;
                  
                  // Première plage: b × P
                  const premierePlage = plages[0];
                  const bPremiere = coeffs.b[premierePlage] || 0;
                  const pPremiere = puissancesParPlage[premierePlage] || 0;
                  total += bPremiere * pPremiere;
                  
                  // Plages suivantes: b × (P_plage - P_plage_précédente)
                  // Note: P doit être >= P_précédente sinon delta = 0
                  for (let i = 1; i < plages.length; i++) {
                    const plage = plages[i];
                    const plagePrecedente = plages[i - 1];
                    const b = coeffs.b[plage] || 0;
                    const p = puissancesParPlage[plage] || 0;
                    const pPrec = puissancesParPlage[plagePrecedente] || 0;
                    const delta = Math.max(0, p - pPrec); // Delta positif uniquement
                    total += b * delta;
                  }
                  
                  return total;
                };
                
                // Pour le calcul actuel, utiliser les puissances actuelles par plage
                const puissancesActuellesObj = pdlConfig?.puissances_actuelles || {};
                const puissancesActuellesPourCalcul = {};
                (coeffsActuels?.plages || []).forEach(plage => {
                  puissancesActuellesPourCalcul[plage] = puissancesActuellesObj[plage] || puissanceSouscrite;
                });
                
                // Pour le calcul proposé, utiliser les puissances préconisées par plage
                const puissancesProposesPourCalcul = {};
                (coeffsSimulation?.plages || []).forEach(plage => {
                  puissancesProposesPourCalcul[plage] = puissancesPreconiseesParPlage[plage] || puissanceProposee || puissanceSouscrite;
                });
                
                const CSFixeActuel = calculerCSFixe(puissancesActuellesPourCalcul, coeffsActuels);
                const CSFixePropose = calculerCSFixe(puissancesProposesPourCalcul, coeffsSimulation);
                
                // CS Variable = Σ(c_plage × E_plage)
                // Utilise les consommations saisies par l'utilisateur
                const consoParPlage = consommationsParPlageRef.current[pdl] || {};
                
                const calculerCSVariable = (coeffs) => {
                  if (!coeffs) return 0;
                  
                  let total = 0;
                  coeffs.plages.forEach(plage => {
                    const c = coeffs.c[plage] || 0;
                    const conso = consoParPlage[plage] || 0;
                    total += c * conso;
                  });
                  
                  return total;
                };
                const CSVariableActuel = calculerCSVariable(coeffsActuels);
                const CSVariablePropose = calculerCSVariable(coeffsSimulation);
                
                // CTA = 15% × (CG + CC + CS fixe)
                const CTAActuel = CTA_TAUX * (CG_Actuel + CC_Actuel + CSFixeActuel);
                const CTAPropose = CTA_TAUX * (CG_Propose + CC_Propose + CSFixePropose);
                
                // CER = Énergie réactive facturable × tarif (HTA uniquement)
                // Tarif CER en c€/kVARh → diviser par 100 pour avoir €/kVARh
                const CER_Actuel_Calcule = (tensionActuelle === 'HTA' && tarifsActuels.CER > 0) 
                  ? energieReactiveFacturable * tarifsActuels.CER / 100 
                  : 0;
                const CER_Propose_Calcule = (tensionSimulation === 'HTA' && tarifsSimulation.CER > 0) 
                  ? energieReactiveFacturable * tarifsSimulation.CER / 100 
                  : 0;
                
                // Utiliser l'override en € si défini, sinon la valeur calculée
                const cerOv = cerOverrideRef.current[pdl] || {};
                const CER_Actuel = cerOv.actuel !== undefined && cerOv.actuel !== '' ? parseFloat(cerOv.actuel) : CER_Actuel_Calcule;
                const CER_Propose = cerOv.propose !== undefined && cerOv.propose !== '' ? parseFloat(cerOv.propose) : CER_Propose_Calcule;
                
                // CMDPS selon formule
                // HTA (formule réglementaire TURPE) : Σ_plage [ α × b_plage × √(Σ ΔP²_plage) ]
                //   → la racine carrée est calculée PAR classe temporelle (plage), le coefficient
                //     pondérateur b est appliqué À L'EXTÉRIEUR de la racine, puis on somme les plages.
                // BTSUP: h × heures_uniques_de_dépassement (extrapolées sur l'année)
                const calculerCMDPS = (dep, tarifs, coeffs) => {
                  if (tarifs.CMDPS_ALPHA && dep.sommeDeltaCarreParPlage) {
                    const alpha = tarifs.CMDPS_ALPHA;
                    let total = 0;
                    Object.entries(dep.sommeDeltaCarreParPlage).forEach(([plage, sommeDP2]) => {
                      if (sommeDP2 > 0) {
                        const b = coeffs?.b?.[plage] || 0;
                        total += alpha * b * Math.sqrt(sommeDP2);
                      }
                    });
                    return total;
                  } else if (tarifs.CMDPS_H && dep.heuresUniques > 0) {
                    // Formule BTSUP: h × heures de dépassement
                    // Extrapoler sur l'année: heuresUniques / nbJours × 365
                    const heuresAnnuelles = dep.heuresUniques * facteurAnnualisation;
                    return tarifs.CMDPS_H * heuresAnnuelles;
                  }
                  return 0;
                };
                const CMDPSActuelCalcule = calculerCMDPS(depActuel, tarifsActuels, coeffsActuels);
                const CMDPSProposeCalcule = calculerCMDPS(depPropose, tarifsSimulation, coeffsSimulation);
                const cmdpsOv = cmdpsOverrideRef.current[pdl] || null;
                const { actuel: CMDPSActuel, propose: CMDPSPropose } = appliquerOverrideCMDPS(cmdpsOv, CMDPSActuelCalcule, CMDPSProposeCalcule);
                const saveCmdps = (champ) => (val) => {
                  const next = { ...(cmdpsOverrideRef.current[pdl] || {}) };
                  if (val === null) delete next[champ]; else next[champ] = val;
                  const all = { ...cmdpsOverrideRef.current };
                  if (Object.keys(next).length) all[pdl] = next; else delete all[pdl];
                  cmdpsOverrideRef.current = all;
                  forceRender(n => n + 1);
                };
                
                // Totaux
                const totalActuel = CG_Actuel + CC_Actuel + CSFixeActuel + CSVariableActuel + CTAActuel + CER_Actuel + CMDPSActuel;
                const totalPropose = CG_Propose + CC_Propose + CSFixePropose + CSVariablePropose + CTAPropose + CER_Propose + CMDPSPropose;
                const economie = totalActuel - totalPropose;
                
                // Sauvegarder les calculs pour le rapport (si pas déjà fait ou si les valeurs ont changé)
                // Préparer les puissances par plage pour le rapport
                const puissancesActuelles = pdlConfig?.puissances_actuelles || {};
                const puissancesProposees = {};
                const plagesFormule = coeffsActuels?.plages || ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'];
                plagesFormule.forEach(plage => {
                  // Utiliser la puissance préconisée par plage si définie, sinon la puissance unique
                  puissancesProposees[plage] = puissancesPreconiseesParPlage[plage] || puissanceProposee || puissanceSouscrite;
                });
                
                const nouveauxCalculs = {
                  pdl,
                  tensionActuelle,
                  formuleActuelle,
                  formuleProposee: formuleSimulation,
                  contrat,
                  puissanceSouscrite,
                  puissanceProposee,
                  puissancePreconisee: puissanceProposee,
                  puissancesActuelles,
                  puissancesProposees,
                  puissancesPreconiseesParPlage: puissancesPreconiseesParPlage, // Ajout des puissances préconisées par plage
                  nbJoursData,
                  facteurAnnualisation,
                  consoAnnuelleKWh,
                  tanPhiMoyen,
                  tanPhiHPH,
                  energieReactiveFacturable,
                  totalActuel,
                  totalPropose,
                  economie,
                  composantes: {
                    'CG': { actuel: CG_Actuel, propose: CG_Propose },
                    'CC': { actuel: CC_Actuel, propose: CC_Propose },
                    'CS Fixe': { actuel: CSFixeActuel, propose: CSFixePropose },
                    'CS Variable': { actuel: CSVariableActuel, propose: CSVariablePropose },
                    'CTA': { actuel: CTAActuel, propose: CTAPropose },
                    'CER': { actuel: CER_Actuel, propose: CER_Propose },
                    'CMDPS': { actuel: CMDPSActuel, propose: CMDPSPropose }
                  },
                  consommations: consoParPlage,
                  depassements: {
                    actuel: depActuel,
                    propose: depPropose
                  },
                  cmdpsOverride: cmdpsOv,
                  cmdpsCalcule: { actuel: CMDPSActuelCalcule, propose: CMDPSProposeCalcule }
                };
                
                // Mettre à jour le state si différent (évite les re-renders infinis)
                if (!calculsRapport || calculsRapport.pdl !== pdl || 
                    calculsRapport.totalActuel !== totalActuel || 
                    calculsRapport.totalPropose !== totalPropose) {
                  setTimeout(() => setCalculsRapport(nouveauxCalculs), 0);
                  
                  // Pour le multi-PDL, stocker aussi dans calculsParPDL
                  if (analyseType === 'multi') {
                    setTimeout(() => {
                      setCalculsParPDL(prev => ({
                        ...prev,
                        [pdl]: nouveauxCalculs
                      }));
                    }, 0);
                  }
                }
                
                // Format monétaire
                const formatEuro = (val) => val > 0 ? `${val.toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €` : '-';
                
                // Unité selon tension
                const unite = tarifsActuels.unite || 'kW';
                const uniteSimulation = tarifsSimulation.unite || 'kW';
                
                // Liste des formules disponibles pour le domaine de tension EN SIMULATION
                // (et non plus le domaine actuel — permet le changement de segment)
                const formulesDisponibles = Object.keys(COEFFICIENTS_PAR_FORMULE).filter(f =>
                  getTensionFromFormule(f) === tensionSimulation
                );

                // Formule par défaut pour chaque segment (lors du changement)
                const FORMULE_DEFAUT_PAR_TENSION = {
                  'HTA': 'HTACU5 PF',
                  'BTSUP': 'BTSUPCU4',
                  'BTINF': 'BTINFCU4',
                };
                // Nature de l'intervention ENEDIS selon le changement de segment.
                // IMPORTANT : on n'affiche AUCUN montant — les frais dépendent des
                // travaux réels et nécessitent un devis ENEDIS officiel.
                const INTERVENTION_CHANGEMENT_SEGMENT = {
                  'HTA→BTSUP': 'Pose ou remplacement de compteur + adaptation du comptage',
                  'HTA→BTINF': 'Pose d\'un compteur + abaissement de puissance — vérifier le raccordement existant',
                  'BTSUP→HTA': 'Raccordement HTA (transformateur, cellule, comptage HTA) — travaux potentiellement lourds',
                  'BTSUP→BTINF': 'Changement de compteur et de disjoncteur — vérifier que la puissance cible ≤ 36 kVA',
                  'BTINF→HTA': 'Raccordement HTA — travaux potentiellement lourds',
                  'BTINF→BTSUP': 'Changement de compteur et augmentation du disjoncteur de branchement',
                };

                const handleChangerSegment = (nouvelleTension) => {
                  if (nouvelleTension === tensionSimulation) return;
                  const cle = `${tensionSimulation}→${nouvelleTension}`;
                  const intervention = INTERVENTION_CHANGEMENT_SEGMENT[cle];
                  const messageFrais = intervention
                    ? `\n\n⚠️ FRAIS ENEDIS À PRÉVOIR\nUn changement de segment peut entraîner des frais ENEDIS.` +
                      `\n• Nature probable de l'intervention : ${intervention}` +
                      `\n• Les montants dépendent des travaux à réaliser et ne peuvent pas être garantis ici.` +
                      `\n• Un devis ENEDIS officiel est nécessaire avant tout engagement.`
                    : '';
                  const ok = window.confirm(
                    `Changement de segment tarifaire\n` +
                    `Domaine actuel proposé : ${tensionSimulation}\n` +
                    `Nouveau domaine : ${nouvelleTension}\n\n` +
                    `Cela remplacera la formule proposée par celle par défaut du segment (${FORMULE_DEFAUT_PAR_TENSION[nouvelleTension]}) ` +
                    `et appliquera les tarifs (CG, CC, CMDPS, CER) correspondants.` +
                    messageFrais +
                    `\n\nContinuer ?`
                  );
                  if (!ok) return;
                  preconisationsPuissanceRef.current = {
                    ...preconisationsPuissanceRef.current,
                    [`${pdl}_formule`]: FORMULE_DEFAUT_PAR_TENSION[nouvelleTension],
                  };
                  // Reset des préconisations par plage car les plages changent selon segment
                  if (preconisationsPuissanceParPlageRef && preconisationsPuissanceParPlageRef.current) {
                    preconisationsPuissanceParPlageRef.current[pdl] = {};
                  }
                  setCalculsAJour(false);
                };
                
                // Composantes pour le tableau
                const composantes = [
                  { 
                    label: 'Composante de Gestion', 
                    valeurActuelle: formatEuro(CG_Actuel),
                    valeurProposee: formatEuro(CG_Propose),
                    numActuel: CG_Actuel,
                    numPropose: CG_Propose
                  },
                  { 
                    label: 'Composante de Comptage', 
                    valeurActuelle: formatEuro(CC_Actuel),
                    valeurProposee: formatEuro(CC_Propose),
                    numActuel: CC_Actuel,
                    numPropose: CC_Propose
                  },
                  { 
                    label: 'Composante de soutirage - part fixe', 
                    valeurActuelle: formatEuro(CSFixeActuel),
                    valeurProposee: formatEuro(CSFixePropose),
                    numActuel: CSFixeActuel,
                    numPropose: CSFixePropose
                  },
                  { 
                    label: 'Composante de soutirage - part variable', 
                    valeurActuelle: formatEuro(CSVariableActuel),
                    valeurProposee: formatEuro(CSVariablePropose),
                    numActuel: CSVariableActuel,
                    numPropose: CSVariablePropose
                  },
                  { 
                    label: 'Contribution Tarifaire d\'Acheminement (15%)', 
                    valeurActuelle: formatEuro(CTAActuel),
                    valeurProposee: formatEuro(CTAPropose),
                    numActuel: CTAActuel,
                    numPropose: CTAPropose
                  },
                  { 
                    label: 'Composante Énergie Réactive', 
                    valeurActuelle: formatEuro(CER_Actuel),
                    valeurProposee: formatEuro(CER_Propose),
                    numActuel: CER_Actuel,
                    numPropose: CER_Propose,
                    isEditable: true,
                    editKey: 'cer',
                    pdlKey: pdl
                  },
                  { 
                    label: 'Composante mensuelle de dépassement', 
                    valeurActuelle: formatEuro(CMDPSActuel),
                    valeurProposee: formatEuro(CMDPSPropose),
                    numActuel: CMDPSActuel,
                    numPropose: CMDPSPropose,
                    isDepassement: true,
                    pdlKey: pdl,
                    calculeActuel: CMDPSActuelCalcule,
                    calculePropose: CMDPSProposeCalcule,
                    manuelActuel: cmdpsOv?.actuel != null,
                    manuelPropose: cmdpsOv?.propose != null,
                    onSaveActuel: saveCmdps('actuel'),
                    onSavePropose: saveCmdps('propose'),
                    hidden: formuleActuelle.startsWith('BTINF')
                  }
                ].filter(c => !c.hidden);
                
                return (
                  <div key={pdl} className="border border-[#BFC6CC] rounded-lg overflow-hidden">
                    <div className="bg-[#397DFF] text-white px-6 py-4">
                      <h3 className="font-heading font-semibold text-lg">PDL {pdl}</h3>
                      <p className="text-sm opacity-90">{tensionActuelle} • {formuleActuelle} • {contrat}</p>
                      <div className="flex gap-4 mt-2">
                        <button onClick={() => setStep(3)} className="text-xs text-white/80 hover:text-white underline" data-testid="edit-pdl-config">
                          Modifier la configuration PDL
                        </button>
                        <button onClick={() => setStep(5)} className="text-xs text-white/80 hover:text-white underline" data-testid="edit-analysis">
                          Modifier l'analyse / puissances
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      {/* Sélecteur de formule tarifaire pour simulation */}
                      <div className="mb-6 p-4 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg">
                        {/* Sélecteur de segment (domaine de tension) */}
                        <div className="mb-4 pb-4 border-b border-[#BFC6CC]">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-[#191D1E]">
                              Segment tarifaire proposé
                            </label>
                            {tensionSimulation !== tensionActuelle && (
                              <span
                                className="text-xs font-semibold text-[#B45309] bg-[#FEF3C7] px-2 py-0.5 rounded"
                                data-testid="segment-changed-badge"
                              >
                                ⚠️ Changement de segment ({tensionActuelle} → {tensionSimulation})
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {[
                              { id: 'HTA', label: 'HTA (C2 / C3)', sub: 'Haute Tension A' },
                              { id: 'BTSUP', label: 'BT > 36 kVA (C4)', sub: 'Basse Tension supérieure' },
                              { id: 'BTINF', label: 'BT ≤ 36 kVA (C5)', sub: 'Basse Tension inférieure' },
                            ].map((seg) => {
                              const active = tensionSimulation === seg.id;
                              const isCurrent = tensionActuelle === seg.id;
                              return (
                                <button
                                  key={seg.id}
                                  data-testid={`btn-segment-${seg.id}`}
                                  type="button"
                                  onClick={() => handleChangerSegment(seg.id)}
                                  className={`flex-1 min-w-[160px] px-4 py-3 rounded-lg border-2 text-left transition ${
                                    active
                                      ? 'border-[#10D183] bg-[#ECFDF5] text-[#047857]'
                                      : 'border-[#BFC6CC] bg-white text-[#687175] hover:border-[#397DFF]'
                                  }`}
                                >
                                  <div className="font-bold text-sm flex items-center gap-2">
                                    {seg.label}
                                    {active && <span className="text-xs">✓</span>}
                                    {isCurrent && !active && (
                                      <span className="text-[10px] text-[#E53935] font-normal">(actuel)</span>
                                    )}
                                  </div>
                                  <div className="text-xs opacity-70">{seg.sub}</div>
                                </button>
                              );
                            })}
                          </div>
                          {tensionSimulation !== tensionActuelle && (
                            <div className="mt-3 p-3 bg-[#FEF3C7] border-l-4 border-[#F59E0B] rounded text-xs text-[#92400E]">
                              <strong>Frais ENEDIS à prévoir :</strong> le changement de segment entraîne des frais d'intervention spécifiques (changement de compteur, voire travaux de raccordement). Ces coûts sont distincts d'une simple modification de Puissance Souscrite. Consultez la <span className="font-semibold">grille tarifaire ENEDIS</span> et confirmez avec un devis officiel avant engagement.
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-[#191D1E] mb-2">
                              Formule Tarifaire Actuelle
                            </label>
                            <div className="px-4 py-2 bg-white border border-[#BFC6CC] rounded-lg text-[#E53935] font-semibold">
                              {formuleActuelle}
                            </div>
                          </div>
                          <div className="text-2xl text-[#687175]">→</div>
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-[#191D1E] mb-2">
                              Formule Tarifaire Proposée
                            </label>
                            <select
                              value={formuleSimulation}
                              onChange={(e) => {
                                preconisationsPuissanceRef.current = {
                                  ...preconisationsPuissanceRef.current,
                                  [`${pdl}_formule`]: e.target.value
                                };
                                setCalculsAJour(false);
                              }}
                              className="w-full px-4 py-2 border border-[#10D183] rounded-lg font-semibold text-[#10D183] bg-white focus:ring-2 focus:ring-[#10D183]"
                              data-testid="select-formule-proposee"
                            >
                              {formulesDisponibles.map(f => (
                                <option key={f} value={f}>{f}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      
                      {/* Indicateurs : Dépassements + Ratio kWh/kW */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                        {formuleActuelle.startsWith('BTINF') ? (
                          <div className="p-3 bg-white border border-[#BFC6CC] rounded-lg col-span-2">
                            <div className="text-xs text-[#687175] mb-1">Dépassements</div>
                            <div className="text-sm text-[#687175] italic">
                              Non applicable en BT Inférieur — le compteur disjoncte en cas de dépassement.
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="p-3 bg-white border border-[#E53935]/30 rounded-lg" data-testid="depassements-actuel">
                              <div className="text-xs text-[#687175] mb-1">Dépassements (Actuel)</div>
                              <div className="text-lg font-bold text-[#E53935]">
                                {depActuel.heuresUniques || 0} <span className="text-xs font-normal">heures</span>
                              </div>
                              <div className="text-xs text-[#687175]">{depActuel.nbPoints || 0} points</div>
                              <div className="text-sm font-mono mt-1 text-[#E53935]">
                                CMDPS: {CMDPSActuel.toLocaleString('fr-FR', {minimumFractionDigits: 0, maximumFractionDigits: 0})} €
                              </div>
                            </div>
                            <div className="p-3 bg-white border border-[#10D183]/30 rounded-lg" data-testid="depassements-propose">
                              <div className="text-xs text-[#687175] mb-1">Dépassements (Proposé)</div>
                              <div className="text-lg font-bold text-[#10D183]">
                                {depPropose.heuresUniques || 0} <span className="text-xs font-normal">heures</span>
                              </div>
                              <div className="text-xs text-[#687175]">{depPropose.nbPoints || 0} points</div>
                              <div className="text-sm font-mono mt-1 text-[#10D183]">
                                CMDPS: {CMDPSPropose.toLocaleString('fr-FR', {minimumFractionDigits: 0, maximumFractionDigits: 0})} €
                              </div>
                            </div>
                          </>
                        )}
                        <div className="p-3 bg-white border border-[#BFC6CC] rounded-lg" data-testid="ratio-kwh-kw-actuel">
                          <div className="text-xs text-[#687175] mb-1">Ratio kWh/kW (Actuel)</div>
                          <div className="text-lg font-bold text-[#191D1E]">
                            {puissanceSouscrite > 0 ? Math.round(consoAnnuelleKWh / puissanceSouscrite).toLocaleString('fr-FR') : '—'}
                          </div>
                          <div className="text-xs text-[#687175]">
                            {Math.round(consoAnnuelleKWh).toLocaleString('fr-FR')} kWh / {puissanceSouscrite} {unite}
                          </div>
                        </div>
                        <div className="p-3 bg-white border border-[#397DFF]/30 rounded-lg" data-testid="ratio-kwh-kw-propose">
                          <div className="text-xs text-[#687175] mb-1">Ratio kWh/kW (Proposé)</div>
                          <div className="text-lg font-bold text-[#397DFF]">
                            {puissanceProposee > 0 ? Math.round(consoAnnuelleKWh / puissanceProposee).toLocaleString('fr-FR') : '—'}
                          </div>
                          <div className="text-xs text-[#687175]">
                            {Math.round(consoAnnuelleKWh).toLocaleString('fr-FR')} kWh / {puissanceProposee} {uniteSimulation}
                          </div>
                        </div>
                      </div>
                      
                      {/* Résumé puissances */}
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-3 bg-[#FEE2E2]/30 border border-[#E53935]/30 rounded-lg text-center">
                          <div className="text-xs text-[#687175]">Puissance Actuelle</div>
                          <div className="text-xl font-bold text-[#E53935]">{puissanceSouscrite > 0 ? `${puissanceSouscrite} ${unite}` : 'Non définie'}</div>
                        </div>
                        <div className="p-3 bg-[#DCFCE7]/30 border border-[#10D183]/30 rounded-lg text-center">
                          <div className="text-xs text-[#687175]">Puissance Proposée</div>
                          <div className="text-xl font-bold text-[#10D183]">{puissanceProposee > 0 ? `${puissanceProposee} ${uniteSimulation}` : 'Non définie'}</div>
                        </div>
                      </div>
                      
                      {/* Énergie réactive : édition directe dans le tableau CER ci-dessous */}
                      
                      {/* Tableau des composantes TURPE */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-[#F5F6F9]">
                              <th className="px-4 py-3 text-left text-[#687175] font-medium">Composante TURPE</th>
                              <th className="px-4 py-3 text-right font-semibold" style={{backgroundColor: '#FEE2E2', color: '#E53935'}}>
                                Situation Actuelle
                              </th>
                              <th className="px-4 py-3 text-right font-semibold" style={{backgroundColor: '#DCFCE7', color: '#10D183'}}>
                                Situation Proposée
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#BFC6CC]">
                            {composantes.map((comp, idx) => {
                              const diff = comp.numPropose - comp.numActuel;
                              const isEconomie = diff < 0;
                              const isSurcout = diff > 0;
                              const isIdentique = diff === 0;
                              
                              return (
                                <tr key={idx} className={comp.isDepassement ? 'bg-[#FEF3C7]/30' : 'hover:bg-[#F5F6F9]'}>
                                  <td className="px-4 py-3 font-medium text-[#191D1E]">
                                    {comp.label}
                                    {comp.isEditable && (
                                      <span className="ml-2 text-xs text-[#397DFF] font-normal">(modifiable)</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-[#191D1E]">
                                    {comp.isEditable ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        defaultValue={cerOverrideRef.current[comp.pdlKey]?.actuel ?? comp.numActuel.toFixed(2)}
                                        onBlur={(e) => {
                                          cerOverrideRef.current = {
                                            ...cerOverrideRef.current,
                                            [comp.pdlKey]: { ...(cerOverrideRef.current[comp.pdlKey] || {}), actuel: e.target.value }
                                          };
                                          setCalculsAJour(false);
                                        }}
                                        className="w-28 px-2 py-1 border border-[#E53935] rounded text-right text-sm font-mono bg-[#FEE2E2]/20 focus:ring-1 focus:ring-[#E53935]"
                                        data-testid={`cer-actuel-${comp.pdlKey}`}
                                      />
                                    ) : comp.isDepassement ? (
                                      <CmdpsOverrideCell value={comp.numActuel} calcule={comp.calculeActuel} manuel={comp.manuelActuel}
                                        onSave={comp.onSaveActuel} testid={`cmdps-actuel-${comp.pdlKey}`} />
                                    ) : (
                                      comp.valeurActuelle
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono">
                                    {comp.isEditable ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        defaultValue={cerOverrideRef.current[comp.pdlKey]?.propose ?? comp.numPropose.toFixed(2)}
                                        onBlur={(e) => {
                                          cerOverrideRef.current = {
                                            ...cerOverrideRef.current,
                                            [comp.pdlKey]: { ...(cerOverrideRef.current[comp.pdlKey] || {}), propose: e.target.value }
                                          };
                                          setCalculsAJour(false);
                                        }}
                                        className="w-28 px-2 py-1 border border-[#10D183] rounded text-right text-sm font-mono bg-[#DCFCE7]/20 focus:ring-1 focus:ring-[#10D183]"
                                        data-testid={`cer-propose-${comp.pdlKey}`}
                                      />
                                    ) : comp.isDepassement ? (
                                      <CmdpsOverrideCell value={comp.numPropose} calcule={comp.calculePropose} manuel={comp.manuelPropose}
                                        onSave={comp.onSavePropose} testid={`cmdps-propose-${comp.pdlKey}`}
                                        valueClassName={isIdentique ? 'text-[#687175]' : isEconomie ? 'text-[#10D183] font-bold' : 'text-[#E53935] font-bold'} />
                                    ) : (
                                      <span className={
                                        isIdentique ? 'text-[#687175]' : 
                                        isEconomie ? 'text-[#10D183] font-bold' : 
                                        isSurcout ? 'text-[#E53935] font-bold' : 
                                        'text-[#191D1E]'
                                      }>
                                        {comp.valeurProposee}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Ligne Total */}
                            <tr className="bg-[#191D1E] text-white font-bold">
                              <td className="px-4 py-4">TOTAL ANNUEL</td>
                              <td className="px-4 py-4 text-right font-mono">{formatEuro(totalActuel)}</td>
                              <td className="px-4 py-4 text-right font-mono">{formatEuro(totalPropose)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Économie */}
                      {economie !== 0 && (
                        <div className={`mt-6 p-4 rounded-lg border-2 ${economie > 0 ? 'bg-[#DCFCE7]/30 border-[#10D183]' : 'bg-[#FEE2E2]/30 border-[#E53935]'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm text-[#687175]">
                                {economie > 0 ? 'Économie annuelle estimée' : 'Surcoût annuel estimé'}
                              </div>
                              <div className={`text-3xl font-bold ${economie > 0 ? 'text-[#10D183]' : 'text-[#E53935]'}`}>
                                {economie > 0 ? '-' : '+'} {Math.abs(economie).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                              </div>
                            </div>
                            {economie > 0 && (
                              <div className="text-right">
                                <div className="text-sm text-[#687175]">Soit par mois</div>
                                <div className="text-xl font-bold text-[#10D183]">
                                  ~ {(economie / 12).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Notes */}
                      <div className="mt-4 p-3 bg-[#F5F6F9] rounded-lg text-xs text-[#687175] space-y-1">
                        <p><strong>Tarifs appliqués :</strong> TURPE 7 {tensionActuelle} (01/08/2025) - Formule {formuleActuelle}</p>
                        <p><strong>Période analysée :</strong> {nbJoursData} jours • Extrapolation annuelle : ×{facteurAnnualisation.toFixed(2)}</p>
                        <p><strong>Consommation estimée :</strong> {(consoAnnuelleKWh/1000).toFixed(1)} MWh/an • Tan φ global : {tanPhiMoyen.toFixed(3)}</p>
                        {tensionActuelle === 'HTA' && (
                          <p><strong>Énergie réactive (CER) :</strong> Tan φ HPH : {tanPhiHPH.toFixed(3)} {tanPhiHPH > TAN_PHI_MAX ? `• Facturable : ${(energieReactiveFacturable/1000).toFixed(1)} MVARh` : '• Non facturable (tan φ ≤ 0.4)'}</p>
                        )}
                        {depActuel.nbPoints > 0 && (
                          <p><strong>Dépassements (actuel) :</strong> {depActuel.nbPoints} points • {tensionActuelle === 'HTA' ? `CMDPS calculée = Σ(α×b×√ΣΔP²) = ${CMDPSActuelCalcule.toFixed(0)} €` : `${depActuel.heuresUniques} heures uniques`}</p>
                        )}
                        {depPropose.nbPoints > 0 && (
                          <p><strong>Dépassements (proposé) :</strong> {depPropose.nbPoints} points • {tensionSimulation === 'HTA' ? `CMDPS calculée = Σ(α×b×√ΣΔP²) = ${CMDPSProposeCalcule.toFixed(0)} €` : `${depPropose.heuresUniques} heures uniques`}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
                    {total > SEUIL_PAGINATION && filtres.length > 0 && (
                      <PdlPagination page={safePage} totalPages={totalPages} total={filtres.length} pageSize={SEUIL_PAGINATION} onPage={setPage} testidPrefix="step6-bottom" />
                    )}
                    {filtres.length === 0 && (
                      <div className="text-center py-8 text-sm text-[#687175]">
                        Aucun PDL ne correspond à « {searchPdl} ».
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="p-12 bg-[#F5F6F9] rounded-lg text-center">
              <p className="text-[#687175]">
                Aucune donnée disponible. Retournez à l'étape 4 pour importer des données brutes.
              </p>
            </div>
          )}

          <div className="flex justify-between gap-3 pt-6">
            <button onClick={() => setStep(5)} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">
              ← Retour
            </button>
            <div className="flex gap-3">
              <button
                onClick={sauvegarderBrouillon}
                className="flex items-center gap-2 px-4 py-2 border border-[#F9C80E] text-[#F9C80E] hover:bg-[#F9C80E] hover:text-white rounded-lg"
              >
                💾 Enregistrer Brouillon
              </button>
              <button
                onClick={() => setStep(7)}
                className="bg-[#10D183] hover:bg-[#0FB978] text-white py-3 px-8 rounded-lg font-semibold"
              >
                Générer le Rapport →
              </button>
            </div>
          </div>
        </div>
  );
};

export default Step6Validation;
